import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import coachnow_logo from "app/images/Logo_dark.svg";
import price_tag from "app/images/green_price_tag.svg";
import PriceCard from "./_components/PriceCard";
import BillingInformation from "./_components/BillingInformation";
import ShowcasePanel from "./_components/ShowcasePanel";
import CheckoutLegalLinks from "app/components/CheckoutLegalLinks";
import { Toast } from "app/components/helpers";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import SuccessPage from "app/components/subscription/Upgrade/SuccessPage";
import { cls } from "app/utils";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Authenticated, Loading } from "app/components/Wrappers";
import PricingToggle, { AnalyzePlanType } from "./_components/PricingToggle";
import { useTranslation } from "react-i18next";
import { AuthService } from "app/services";
import { useStoreActions } from "app/utils/hooks";
import { fetchConnectedCoaches } from "app/actions/connection";
import { Currency, formatPrice } from "app/utils/currency";
import { usePromoOffer } from "app/hooks/usePromoOffer";
import PromoResolveError from "app/components/PromoResolveError";
import { hasCanceledStorePlan, repriceTargets } from "app/utils/subscription";
import {
  mergePaywallContent,
  paywallBillingTerm,
  usePaywallFlag,
} from "app/hooks/usePaywallFlag";

const ANALYZE_PLAN_CODES: AnalyzePlanType[] = ["analyze+yearly", "analyze+monthly"];

// Module-level so its identity is stable across renders; defining it inside
// the component would remount the whole subtree (incl. the Stripe form) on
// every re-render.
//
// An athlete invited by a coach is sent home; anyone else may buy Analyze, a
// coach included (a downgrade from PRO).
const UninvitedContent = ({
  userIsAthleteSide,
  hasInvitingCoach,
  children,
}: {
  userIsAthleteSide: boolean;
  hasInvitingCoach: boolean;
  children: React.ReactNode;
}) => {
  if (userIsAthleteSide && hasInvitingCoach) {
    return <Redirect to="/" />;
  }
  return <>{children}</>;
};

// `purchase` renders the direct-buy variant (/purchase/analyze): the checkout
// charges immediately instead of starting a trial, with matching copy.
const AnalyzeCheckout = ({ purchase = false }: { purchase?: boolean }) => {
  const Auth: any = AuthService;
  const { t } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user);
  const [toastAlert, setToastAlert] = useState(null);
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [repricedPlan, setRepricedPlan] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const linkedPromo = params.get("promo") || undefined;
  // The code dropped for locking onto a term this page may not move to (see
  // below); a different code arriving on the same route is resolved as usual.
  const [droppedPromo, setDroppedPromo] = useState<string>();
  const promo = linkedPromo === droppedPromo ? undefined : linkedPromo;
  // Analyze is USD-only: ignore any ?currency= so regional trial defaults and a
  // non-USD checkout can't kick in.
  const currency: Currency = "usd";
  const hasPromo = Boolean(promo);
  // Athlete-side accounts: athletes and parents/fans ("other").
  const userIsAthleteSide = ["athlete", "other"].includes(
    self?.kind?.toLowerCase(),
  );
  // A direct purchase by a user who already pays Stripe for a plan re-prices
  // that subscription in place (plan.upgradeTargets) instead of buying anew:
  // the other Analyze term. The page then sells only the terms the
  // subscription may move to.
  const repriceTerms = purchase ? (repriceTargets(self, ANALYZE_PLAN_CODES) as AnalyzePlanType[]) : [];
  const reprice = repriceTerms.length > 0;

  // An athlete invited by a coach is sent home (UninvitedContent): that coach
  // is in their connections list, a self-signed athlete's list is empty. The
  // check is athlete-only — a coach buying Analyze is not gated by it.
  const { connection } = useStoreActions({ connection: fetchConnectedCoaches });
  const connectedCoaches = connection?.data;
  // On a failed request `data` never arrives — fail open (render the page)
  // instead of trapping the user on the spinner; the invited-athlete redirect
  // is a soft gate, not a security boundary.
  const connectionsLoading =
    connection?.pending || (connectedCoaches == null && !connection?.error);
  const hasInvitingCoach =
    Array.isArray(connectedCoaches) && connectedCoaches.length > 0;

  const hasActiveTrial = self?.subscription?.trialEndsAt >= new Date();
  // A plan still renewing in a store keeps the page closed until it is
  // cancelled there; one already cancelled does not stand in the way of a
  // Stripe plan for the time it has left. A trial never follows a live plan.
  const canceledStorePlan = purchase && hasCanceledStorePlan(self);
  const sendHome =
    !reprice &&
    !canceledStorePlan &&
    (hasActiveTrial ||
      self?.subscription?.status === "active" ||
      self?.subscription?.status === "trial" ||
      self?.hasActiveSubscription ||
      self?.hasTrialSubscription);
  // The trial paywall's copy can come from its GrowthBook flag, which is read
  // only once the paywall is on screen.
  const { flag: paywallFlag, ready: contentReady } = usePaywallFlag(
    "paywall-analyze",
    {
      shown:
        Boolean(self) &&
        !purchase &&
        !sendHome &&
        !(userIsAthleteSide && (connectionsLoading || hasInvitingCoach)),
      user: self,
    },
  );
  // The term picked on the toggle; until then, the one the flag opens on.
  const [pickedPlan, setPlanCode] = useState<AnalyzePlanType>();
  const planCode: AnalyzePlanType =
    pickedPlan ??
    (paywallBillingTerm(paywallFlag) === "monthly"
      ? "analyze+monthly"
      : "analyze+yearly");

  // Resolve the plan's price + any offer for the current (code, plan), waiting
  // for auth. The hook owns the resolve dispatch + freshness / loading / error /
  // retry (shared across the checkout routes).
  const { offer, baseAmount, loading, error: resolveError, lockedPlan, trialDays: promoTrialDays, resolveOffer } = usePromoOffer({
    code: promo,
    planCode,
    currency,
    ready: !!self,
  });
  // A term-locked code (tied to one billing term) resolves to that term's plan;
  // follow it so a locked link doesn't hang on the requested term's loader. A
  // lock onto a term this page may not move to (the plan the user is on) is
  // dropped with its code, as the API drops it: the page then prices the term
  // it can sell at the full price instead of waiting on a resolution the lock
  // never lets match.
  useEffect(() => {
    if (!lockedPlan) {
      return;
    }
    if (reprice && !repriceTerms.includes(lockedPlan as AnalyzePlanType)) {
      setDroppedPromo(linkedPromo);
    } else if (lockedPlan !== planCode) {
      setPlanCode(lockedPlan as AnalyzePlanType);
    }
    // planCode is a dep so a term toggle on a locked code re-asserts the lock
    // (snaps back) instead of leaving the page stuck on the mismatched term.
  }, [lockedPlan, planCode, reprice, repriceTerms]);
  useEffect(() => {
    if (reprice && !repriceTerms.includes(planCode)) {
      setPlanCode(repriceTerms[0]);
    }
  }, [reprice, repriceTerms, planCode]);
  const hasOffer = Boolean(offer);
  // Backend-resolved (region-aware); 7 is a transient fallback shown only while
  // the resolve is in flight (the dynamic line is hidden during loading).
  const trialDays = promoTrialDays ?? 7;
  // Today's copy, which the flag's fields replace one by one.
  const content = mergePaywallContent(
    {
      headline: t("Start your Free {{trialDays}}-Day Trial", { trialDays }),
      subhead: t(
        "Your payment will process after the {{trialDays}}-day free trial period. Cancel at any time.",
        { trialDays },
      ),
      annual_badge: "-50%",
      plan_description: t(
        "Tailor-built for athletes, parents, or new coaches that desire world-class video analysis without the elite price tag. The perfect plan to get started with CoachNow.",
      ),
      trial_headline: t("{{trialDays}} Days Free", { trialDays }),
      cta_text: t("Start Free Trial"),
      show_free_plan_button: true,
      free_plan_text: t("Continue with Free Plan"),
      social_proof_headline: `20,000+ ⭐${t("5-Star Reviews,")}\n${t("4.9 Average Rating")}`,
      feature_card_tagline: t(
        "Tailor-built for athletes, parents, or new coaches that desire world-class video and image analysis without the elite price tag.",
      ),
      feature_list: [
        t("5 Spaces & Groups"),
        t("Video/Image Analysis Suite"),
        t("250 Media Items in Library"),
      ],
    },
    paywallFlag,
    trialDays,
  );

  const onDone = (_token?: unknown, didReprice?: boolean) => {
    if (purchase) {
      // What just happened, as the form saw it: the refreshed account is on the
      // new plan and has re-price targets of its own.
      setRepricedPlan(Boolean(didReprice));
      setShowSuccessPage(true);
    } else {
      setToastAlert(t("Your account was successfully updated."));
    }
  };

  const onPlanCodeChange = useCallback((code: AnalyzePlanType) => {
    setPlanCode(code);
  }, []);

  // Short description of what the applied promo grants, mirroring the pro page.
  const couponDescription = useMemo(() => {
    const promoDiscount = offer?.discount;
    let discount: string | null = null;
    if (promoDiscount?.percentOff) {
      if (promoDiscount.duration === "forever") {
        discount = t("{{percent}}% off for life", { percent: promoDiscount.percentOff });
      } else if (promoDiscount.duration === "repeating" && promoDiscount.durationInMonths) {
        discount = t("{{percent}}% off for the first {{months}} months", {
          percent: promoDiscount.percentOff,
          months: promoDiscount.durationInMonths,
        });
      } else {
        discount = t("{{percent}}% off your first payment", {
          percent: promoDiscount.percentOff,
        });
      }
    } else if (promoDiscount?.amountOff) {
      discount = t("{{amount}} off your first payment", {
        amount: formatPrice(promoDiscount.amountOff, currency),
      });
    }

    // A direct purchase never grants a trial, so only the discount matters.
    const showTrial = !purchase && trialDays > 7;
    if (showTrial && discount) {
      return t("{{trialDays}} days free, then {{discount}}", { trialDays, discount });
    }
    if (showTrial) {
      return t("{{trialDays}} days free", { trialDays });
    }
    return discount;
  }, [offer, trialDays, currency, purchase, t]);

  // Checked before the subscription gate: once the purchase lands the user
  // HAS a subscription, and the gate would otherwise steal the success screen.
  if (showSuccessPage) {
    return (
      <Authenticated>
        <SuccessPage
          plan={planCode}
          user={self}
          reprice={repricedPlan}
          onDone={() => history.push("/")}
        />
      </Authenticated>
    );
  }

  if (sendHome) {
    return <Redirect to="/" />;
  }

  // Also held while the flag loads, so the defaults don't flash before the
  // variant.
  if (
    (Auth.isAuthenticated && !self) ||
    (userIsAthleteSide && connectionsLoading) ||
    !contentReady
  ) {
    return (
      <div className={styles.loading}>
        <Loading isLoading loadType="spinner" />
      </div>
    );
  }

  return (
    <Authenticated redirect="/signup">
      <UninvitedContent
        userIsAthleteSide={userIsAthleteSide}
        hasInvitingCoach={hasInvitingCoach}
      >
        <div
          className={cls(styles.container, "analyze-trial")}
        >
          <div className={styles.left_content}>
            <img
              className={styles.site_logo}
              src={coachnow_logo}
              alt="CoachNow Icon"
            />
            <div className={styles.content_container}>
              <div className={styles.header}>
                <h3 className={styles.header_title}>
                  {reprice
                    ? t("Switch your CoachNow Analyze plan")
                    : purchase
                    ? t("Start your CoachNow Analyze subscription")
                    : loading
                    ? t("Start your Free Trial")
                    : content.headline}
                </h3>
                <p className={styles.header_description}>
                  {reprice
                    ? t("Your current plan's unused time is credited toward this payment. If the credit is larger than the payment, the rest stays on your account and covers your next bills.")
                    : purchase
                    ? t("To get started enter your card details")
                    : loading
                    ? t(
                        "Your payment will process after your free trial period. Cancel at any time.",
                      )
                    : content.subhead}
                </p>
              </div>
              {!loading && hasOffer && (
                <div className={styles.promo_applied_container}>
                  <div className={styles.promocode_applied}>
                    <img
                      src={price_tag}
                      alt="coupon tag"
                      className={styles.price_tag}
                    />
                    <div className={styles.promo_applied_wrapper}>
                      <p className={styles.promo_applied}>
                        {t("Special Offer Applied!")}
                      </p>
                      {couponDescription && (
                        <p className={styles.coupon_description}>
                          {couponDescription}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <PricingToggle
                planCode={planCode}
                onPlanSelect={onPlanCodeChange}
                discount={hasPromo ? undefined : content.annual_badge || undefined}
                availablePlans={reprice ? repriceTerms : undefined}
              />
              {resolveError ? (
                <PromoResolveError onRetry={resolveOffer} />
              ) : (
                <PriceCard
                  planCode={planCode}
                  loading={loading || baseAmount == null}
                  baseAmount={baseAmount}
                  discount={offer?.discount}
                  purchase={purchase}
                  description={content.plan_description}
                  trialHeadline={content.trial_headline}
                />
              )}
              <BillingInformation
                onDone={onDone}
                planCode={planCode}
                couponCode={promo}
                currency={currency}
                purchase={purchase}
                reprice={reprice}
                ctaText={content.cta_text}
                showFreePlanButton={content.show_free_plan_button}
                freePlanText={content.free_plan_text}
              />
              <CheckoutLegalLinks />
            </div>
            <Toast success={toastAlert} onDone={() => setToastAlert(null)} />
          </div>
          <div className={styles.right_content}>
            <ShowcasePanel
              headline={content.social_proof_headline}
              tagline={content.feature_card_tagline}
              features={content.feature_list}
            />
          </div>
        </div>
      </UninvitedContent>
    </Authenticated>
  );
};

export default AnalyzeCheckout;
